import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/** Fraction of a section that must be on screen before it counts as the one being read. */
const IN_VIEW_THRESHOLD = 0;

/**
 * Sticky in-page nav for the single-concern template.
 *
 * Builds its own items from every concern section carrying `data-concern-nav-label`, in DOM
 * order, so the merchant reordering or removing sections in the theme editor needs no matching
 * change here. Appears once the hero has scrolled away and tracks the section in view.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} bar - The fixed bar. Stays hidden until there is something to show.
 * @property {HTMLElement} list - The <ul> the items are written into.
 *
 * @extends {Component<Refs>}
 */
class ConcernNav extends Component {
  requiredRefs = ['bar', 'list'];

  /** @type {Map<Element, HTMLAnchorElement>} */
  #links = new Map();

  /** @type {IntersectionObserver | null} */
  #sectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #heroObserver = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {AbortController | null} */
  #listeners = null;

  /** Sections currently intersecting the viewport, in DOM order. */
  #inView = new Set();

  connectedCallback() {
    super.connectedCallback();

    this.#listeners?.abort();
    const { signal } = (this.#listeners = new AbortController());

    this.refs.list.addEventListener('click', this.#onClick, { signal });

    // Sections are rendered by sibling sections, so wait for the whole document rather than just
    // this element. In the theme editor a section can also arrive later, hence the re-scan below.
    document.addEventListener('shopify:section:load', this.#build, { signal });
    document.addEventListener('shopify:section:unload', this.#build, { signal });

    this.#build();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#teardownObservers();
    this.#listeners?.abort();
    this.#listeners = null;
    this.#links.clear();
    this.#inView.clear();
    document.documentElement.style.removeProperty('--concern-anchor-offset');
  }

  #teardownObservers() {
    this.#sectionObserver?.disconnect();
    this.#sectionObserver = null;
    this.#heroObserver?.disconnect();
    this.#heroObserver = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  /** Rebuilds the item list from the sections currently on the page. */
  #build = () => {
    this.#teardownObservers();
    this.#inView.clear();
    this.#links.clear();

    const targets = /** @type {HTMLElement[]} */ ([
      ...document.querySelectorAll('[data-concern-nav-label][id]'),
    ]).filter((element) => element.dataset.concernNavLabel?.trim());

    const list = this.refs.list;
    list.replaceChildren();

    if (targets.length < 2) {
      // One destination is not a nav.
      this.refs.bar.hidden = true;
      return;
    }

    for (const target of targets) {
      const item = document.createElement('li');
      const link = document.createElement('a');

      link.className = 'concern-nav__link';
      link.href = `#${target.id}`;
      link.textContent = /** @type {string} */ (target.dataset.concernNavLabel).trim();

      item.append(link);
      list.append(item);
      this.#links.set(target, link);
    }

    this.refs.bar.hidden = false;

    this.#sectionObserver = new IntersectionObserver(this.#onSectionIntersect, {
      threshold: IN_VIEW_THRESHOLD,
      // Only the band under the bar counts as "in view", so the highlight matches what is being read.
      rootMargin: `-${Math.round(this.#topOffset + this.refs.bar.offsetHeight)}px 0px -55% 0px`,
    });

    for (const target of this.#links.keys()) this.#sectionObserver.observe(target);

    this.#watchHero();
    this.#syncOffsets();

    this.#resizeObserver = new ResizeObserver(() => this.#syncOffsets());
    this.#resizeObserver.observe(this.refs.bar);
  };

  /**
   * Reveals the bar once the hero has left the viewport. With no hero on the page the bar is
   * shown immediately, which is the safe default — this is the page's main route to the products.
   */
  #watchHero() {
    const hero = document.getElementById('concern-hero');

    if (!hero) {
      this.#setVisible(true);
      return;
    }

    this.#heroObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry) this.#setVisible(!entry.isIntersecting);
      },
      { threshold: 0 }
    );

    this.#heroObserver.observe(hero);
  }

  /** @param {boolean} visible */
  #setVisible(visible) {
    this.refs.bar.dataset.visible = visible ? 'true' : 'false';
  }

  /** Height of anything pinned above the bar. */
  get #topOffset() {
    const extra = Number(this.dataset.offset) || 0;

    if (this.dataset.followHeader !== 'true') return extra;

    const header = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height'));

    return (Number.isFinite(header) ? header : 0) + extra;
  }

  /**
   * Publishes the bar's own footprint so anchor targets can reserve room for it, and positions
   * the bar under the header.
   */
  #syncOffsets() {
    const top = this.#topOffset;

    this.style.setProperty('--concern-nav-top', `${top}px`);
    document.documentElement.style.setProperty(
      '--concern-anchor-offset',
      `${top + this.refs.bar.offsetHeight}px`
    );
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onSectionIntersect = (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) this.#inView.add(entry.target);
      else this.#inView.delete(entry.target);
    }

    // Several sections can be in the band at once; the first in DOM order is the one being read.
    let current = null;

    for (const target of this.#links.keys()) {
      if (this.#inView.has(target)) {
        current = target;
        break;
      }
    }

    for (const [target, link] of this.#links) {
      if (target === current) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    }

    if (current) this.#scrollLinkIntoView(/** @type {HTMLAnchorElement} */ (this.#links.get(current)));
  };

  /** Keeps the active pill visible in the mobile scroller. @param {HTMLAnchorElement} link */
  #scrollLinkIntoView(link) {
    const list = this.refs.list;
    if (list.scrollWidth <= list.clientWidth) return;

    const target = Math.max(0, link.offsetLeft - (list.clientWidth - link.offsetWidth) / 2);

    list.scrollTo({ left: target, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const link = /** @type {HTMLElement | null} */ (event.target)?.closest?.('a[href^="#"]');
    if (!(link instanceof HTMLAnchorElement)) return;

    const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
    if (!target) return;

    event.preventDefault();

    const top = target.getBoundingClientRect().top + window.scrollY - this.#topOffset - this.refs.bar.offsetHeight;

    window.scrollTo({ top, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });

    // Keep the URL and the focus ring honest, without letting the hash change re-scroll the page.
    history.replaceState(null, '', link.hash);
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  };
}

if (!customElements.get('concern-nav-component')) {
  customElements.define('concern-nav-component', ConcernNav);
}
